import React, { useState, useEffect, useRef } from 'react';
import GridLayout from 'react-grid-layout';
import { Switch, Snackbar, Alert } from '@mui/material';
// Types used throughout the app
import type { Table, Wall, GridSize } from '../types';
import { useHistory } from '../hooks/useHistory';
import ScenarioModal from './ScenarioModal/ScenarioModal';
import ToastNotification from './ToastNotification/ToastNotification';
import TableModal from './TableModal/TableModal';
import TableList from './TableList/TableList';
import Toolbar from './Toolbar/Toolbar';
import SystemBar from './SystemBar/SystemBar';
import Header from './Header/Header';
import WallsLayer from './WallsLayer/WallsLayer';
import TableGrid from './TableGrid/TableGrid';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';
import { useScenarioPersistence } from '../hooks/useScenarioPersistence';
import { useTableActions } from '../hooks/useTableActions';
import { useAuth } from '../auth/AuthContext';
import type { ScenarioEditPresenceRecord, ScenarioSnapshot } from '../data/types';
import { canDeleteScenario, canSeeAppMenu, canSetDefaultScenario } from '../auth/roles';
import SystemStatusBanner from './SystemStatusBanner/SystemStatusBanner';
import ScenarioPresenceBanner from './ScenarioPresenceBanner/ScenarioPresenceBanner';
import ManageUsersModal from './ManageUsersModal/ManageUsersModal';
import ProfileModal from './ProfileModal/ProfileModal';
import ScenarioSettingsModal from './ScenarioSettingsModal/ScenarioSettingsModal';
import { useI18n } from '../i18n/I18nContext';
import SensitiveAccessModal from './SensitiveAccessModal/SensitiveAccessModal';
import ManageProductsModal from './ManageProductsModal/ManageProductsModal';
import ManageProductCategoriesModal from './ManageProductCategoriesModal/ManageProductCategoriesModal';
import ToolModeBanner from './ToolModeBanner/ToolModeBanner';
import DenyAccessReasonModal from './DenyAccessReasonModal/DenyAccessReasonModal';
import ConfirmActionModal from './ConfirmActionModal/ConfirmActionModal';
import {
  canMutateCurrentScenarioOnline,
  getPresencePollIntervalMs,
  shouldAutoHealSoloEditorConflict,
  shouldAutoClaimScenario as shouldAutoClaimScenarioState,
  shouldRecoverEditorLock as shouldRecoverEditorLockState,
  shouldRefreshScenarioForPresence,
  shouldShowScenarioPresenceBanner as shouldShowScenarioPresenceBannerState,
} from './TableArrangementApp/collaborationState';
import {
  acquireScenarioEditLock,
  flushScenarioWrites,
  getLatestConflictScenarioDraft,
  keepLatestConflictAsScenarioCopy,
  loadScenarioBaseState,
  loadScenarioEditPresence,
  overwriteLatestScenarioConflictWithLocalSnapshot,
  releaseScenarioEditLock,
  renewScenarioEditLock,
  requestScenarioEditAccess,
  resolveLatestScenarioConflict,
  respondScenarioEditAccessRequest,
  saveScenarioBaseState as saveScenarioBaseStateRequest,
  setScenarioReleasePending,
  bootstrapFromServer,
  loadScenarioSnapshot,
  loadSyncState,
  syncPendingMutations,
} from '../data/repository';
import {
  REALTIME_SCENARIO_PRESENCE_EVENT,
  REALTIME_SCENARIO_SNAPSHOT_EVENT,
  type RealtimeEventPayload,
} from '../realtime/events';

// External styles
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
if (typeof window !== 'undefined') {
  (window as any).MaterialUI = { Switch, Snackbar, Alert };
  (window as any).ReactGridLayout = GridLayout;
}


const currentVersion = '1.0.14';

function uniqueId() {
  return '_' + Math.random().toString(36).substr(2, 9);
}

function TableArrangementApp() {
  const { t } = useI18n();
  const {
    auth,
    logout,
    authorizeSensitivePassword,
    menuItems: remoteMenuItems,
    refreshFromLocal,
    scenario: authScenario,
    backendReachable,
    syncState,
  } = useAuth();
  const currentUser = auth?.user ?? null;
  const effectiveMenuItems = remoteMenuItems;
  const [scenarioName, setScenarioName] = useState<string>('');
  const [loadedScenario, setLoadedScenario] = useState<boolean>(false);
  const [scenarioVersion, setScenarioVersion] = useState<number | null>(null);
  const [gridSize, setGridSize] = useState<GridSize>({ cols: 16, rows: 10 });
  const [walls, setWalls] = useState<Wall[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [showPrices, setShowPrices] = useState(false);
  const [itemizePrintItems, setItemizePrintItems] = useState(false);
  const [mode, setMode] = useState<'wall' | 'delete' | null>(null);
  const [showScenarioModal, setShowScenarioModal] = useState<boolean>(false);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const {
    darkMode,
    currentTableColor,
    currentWallColor,
    toggleDarkMode,
    updateTableColor,
    updateWallColor,
  } = useDisplayPreferences(currentUser);
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const [scenarioPresence, setScenarioPresence] = useState<ScenarioEditPresenceRecord | null>(null);
  const [scenarioPresenceLoading, setScenarioPresenceLoading] = useState(false);
  const [scenarioPresenceActionLoading, setScenarioPresenceActionLoading] = useState(false);
  const scenarioPresenceRef = useRef<ScenarioEditPresenceRecord | null>(null);
  const scenarioVersionRef = useRef<number | null>(null);
  const backendReachableRef = useRef(backendReachable);
  const syncRetryKeyRef = useRef<string | null>(null);
  const soloConflictHealKeyRef = useRef<string | null>(null);
  const scenarioAutoClaimWindowRef = useRef<{ scenarioName: string; expiresAt: number } | null>(null);
  const [historySyncToken, setHistorySyncToken] = useState(0);
  const appliedHistorySyncTokenRef = useRef(0);

  function showToast(message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success'): void {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  }

  useEffect(() => {
    scenarioPresenceRef.current = scenarioPresence;
  }, [scenarioPresence]);

  useEffect(() => {
    scenarioVersionRef.current = scenarioVersion;
  }, [scenarioVersion]);

  backendReachableRef.current = backendReachable;

  function shouldAutoClaimScenario(nextPresence: ScenarioEditPresenceRecord | null): boolean {
    return shouldAutoClaimScenarioState({
      nextPresence,
      previousPresence: scenarioPresenceRef.current,
      backendReachable: backendReachableRef.current,
      claimWindow: scenarioAutoClaimWindowRef.current,
      nowMs: Date.now(),
    });
  }

  function shouldRecoverEditorLock(nextPresence: ScenarioEditPresenceRecord | null): boolean {
    return shouldRecoverEditorLockState({
      nextPresence,
      previousPresence: scenarioPresenceRef.current,
      backendReachable: backendReachableRef.current,
    });
  }

  function settleScenarioAutoClaim(nextPresence: ScenarioEditPresenceRecord | null): void {
    if (!nextPresence) {
      return;
    }

    const claimWindow = scenarioAutoClaimWindowRef.current;
    if (!claimWindow || claimWindow.scenarioName !== nextPresence.scenarioName) {
      return;
    }

    if (nextPresence.isEditor || nextPresence.lockActive || Date.now() > claimWindow.expiresAt) {
      scenarioAutoClaimWindowRef.current = null;
    }
  }

  useEffect(() => {
    if (!loadedScenario || !scenarioName || !auth?.authenticated || !backendReachable) {
      scenarioAutoClaimWindowRef.current = null;
      return;
    }

    scenarioAutoClaimWindowRef.current = {
      scenarioName,
      expiresAt: Date.now() + 3_000,
    };
  }, [auth?.authenticated, backendReachable, loadedScenario, scenarioName]);

  // Exit drawing or delete modes when the Escape key is pressed.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape' && (mode === 'wall' || mode === 'delete')) {
        setMode(null);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mode]);

  const headerHCalc = headerRef.current ? headerRef.current.clientHeight : 60;
  const availableHeight = typeof window !== 'undefined' ? window.innerHeight - headerHCalc : 600;
  // Height of the ReactGridLayout container. Equal to the available height below the header.
  const rglHeightPx = availableHeight;

  useEffect(() => {
    function updateRows() {
      const headerH = headerRef.current ? headerRef.current.clientHeight : 60;
      const availHeight = window.innerHeight - headerH;
      const baseRowHeight = 50;
      const computedRows = Math.max(1, Math.floor(availHeight / baseRowHeight));
      if (computedRows !== gridSize.rows) {
        setGridSize(prev => ({ ...prev, rows: computedRows }));
      }
    }
    updateRows();
    window.addEventListener('resize', updateRows);
    return () => window.removeEventListener('resize', updateRows);
  }, []);

  const {
    saveScenario,
    loadScenarioByName,
    deleteCurrentScenario,
    archiveCurrentScenario,
    restoreArchivedScenario,
    editScenario,
    createScenario,
    duplicateScenario,
    loadRecentScenario,
    tryAutoLoadScenario,
    refreshScenarioNamesFromServer,
    scenarioNames,
    archivedScenarioNames,
    recentScenarioNames,
  } = useScenarioPersistence({
    currentUser,
    scenarioName,
    loadedScenario,
    scenarioVersion,
    gridSize,
    walls,
    tables,
    showPrices,
    itemizePrintItems,
    setScenarioName,
    setLoadedScenario,
    setGridSize,
    setWalls,
    setTables,
    setShowPrices,
    setItemizePrintItems,
    showToast,
  });

  const {
    history,
    historyIndex,
    historyReady,
    lastActionMessage,
    pushHistory,
    syncHistoryToSnapshot,
    undo,
    redo,
  } = useHistory(loadedScenario ? scenarioName : null);

  // Track whether a table has recently been dragged or resized. We
  // store this flag in a ref rather than state so that click
  // handlers always see the latest value. When a drag starts
  // the flag is set to true, and we clear it shortly after the
  // drag ends. This prevents the onClick handler from firing
  // immediately after a drag or resize.
  const isDragging = useRef<boolean>(false);
  const {
    selectedTable,
    showTableModal,
    openTableModal,
    closeTableModal,
    addTable,
    updateTable,
    deleteTable,
    reorderTables,
  } = useTableActions({
    tables,
    walls,
    gridSize,
    setTables,
    pushHistory,
    saveScenario,
  });

  // On initial mount, attempt to automatically load a scenario. Priority:
  // default scenario (if configured), then most recently used, then the first saved scenario.
  useEffect(() => {
    if (loadedScenario) return;
    tryAutoLoadScenario();
  }, [loadedScenario, tryAutoLoadScenario]);

  useEffect(() => {
    if (loadedScenario || !authScenario) {
      return;
    }
    setScenarioName(authScenario.scenarioName);
    setScenarioVersion(authScenario.currentVersion ?? null);
    setGridSize(authScenario.gridSize);
    setWalls(authScenario.walls);
    setTables(authScenario.tables);
    setShowPrices(authScenario.showPrices);
    setItemizePrintItems(authScenario.itemizePrintItems);
    setLoadedScenario(true);
  }, [
    authScenario,
    loadedScenario,
    setGridSize,
    setItemizePrintItems,
    setLoadedScenario,
    setScenarioName,
    setScenarioVersion,
    setShowPrices,
    setTables,
    setWalls,
  ]);

  useEffect(() => {
    if (!loadedScenario || !scenarioName) {
      appliedHistorySyncTokenRef.current = 0;
      return;
    }
    setHistorySyncToken((current) => current + 1);
  }, [loadedScenario, scenarioName]);

  useEffect(() => {
    if (!historyReady || !loadedScenario || !scenarioName || historySyncToken === 0) {
      return;
    }

    if (appliedHistorySyncTokenRef.current === historySyncToken) {
      return;
    }

    appliedHistorySyncTokenRef.current = historySyncToken;
    syncHistoryToSnapshot(tables, walls, t('table.initialHistory'));
  }, [historyReady, historySyncToken, loadedScenario, scenarioName, syncHistoryToSnapshot, t, tables, walls]);

  function toggleWallTool(): void {
    setMode(prev => (prev === 'wall' ? null : 'wall'));
    // Cancel any draft if we switch modes. Draft state is managed in WallsLayer.
  }

  function toggleDeleteTool(): void {
    setMode(prev => (prev === 'delete' ? null : 'delete'));
    // Cancel any active drawing when switching tools. Draft state is managed in WallsLayer.
  }


  function handleUndo(): void {
    if (!canMutateCurrentScenario) {
      return;
    }
    if (historyIndex <= 0) {
      return;
    }
    void undo()
      .then((snapshot) => {
        if (snapshot) {
          applyScenarioSnapshot(snapshot);
        }
      })
      .catch((error) => {
        showToast(error instanceof Error ? error.message : 'Unable to undo.', 'error');
      });
  }

  function handleRedo(): void {
    if (!canMutateCurrentScenario) {
      return;
    }
    if (historyIndex >= history.length - 1) {
      return;
    }
    void redo()
      .then((snapshot) => {
        if (snapshot) {
          applyScenarioSnapshot(snapshot);
        }
      })
      .catch((error) => {
        showToast(error instanceof Error ? error.message : 'Unable to redo.', 'error');
      });
  }

  function handleChangeTableColor(e: React.ChangeEvent<HTMLInputElement>): void {
    if (!canMutateCurrentScenario) {
      return;
    }
    const value = e.target.value;
    updateTableColor(value);
  }
  function handleChangeWallColor(e: React.ChangeEvent<HTMLInputElement>): void {
    if (!canMutateCurrentScenario) {
      return;
    }
    const value = e.target.value;
    updateWallColor(value);
  }


  const [showManageUsersModal, setShowManageUsersModal] = useState<boolean>(false);
  const [showManageProductsModal, setShowManageProductsModal] = useState<boolean>(false);
  const [showManageProductCategoriesModal, setShowManageProductCategoriesModal] = useState<boolean>(false);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [showArchivedScenariosModal, setShowArchivedScenariosModal] = useState<boolean>(false);
  const [showScenarioSettingsModal, setShowScenarioSettingsModal] = useState<boolean>(false);
  const [showDenyAccessReasonModal, setShowDenyAccessReasonModal] = useState(false);
  const [showDeleteScenarioModal, setShowDeleteScenarioModal] = useState(false);
  const [deleteScenarioLoading, setDeleteScenarioLoading] = useState(false);
  const [conflictActionLoading, setConflictActionLoading] = useState(false);
  const [scenarioSettingsMode, setScenarioSettingsMode] = useState<'create' | 'edit' | 'duplicate' | 'conflictCopy'>('edit');
  const [scenarioSettingsInitialName, setScenarioSettingsInitialName] = useState<string>('');
  const [scenarioSettingsInitialGridSize, setScenarioSettingsInitialGridSize] = useState<GridSize>({ cols: 16, rows: 17 });
  const [scenarioSettingsInitialIsDefault, setScenarioSettingsInitialIsDefault] = useState<boolean>(false);
  const [scenarioSettingsInitialShowPrices, setScenarioSettingsInitialShowPrices] = useState<boolean>(false);
  const [scenarioSettingsInitialItemizePrintItems, setScenarioSettingsInitialItemizePrintItems] = useState<boolean>(false);
  const [pendingSensitiveModal, setPendingSensitiveModal] = useState<'profile' | 'users' | 'products' | 'productCategories' | null>(null);
  const [authorizingSensitiveAccess, setAuthorizingSensitiveAccess] = useState(false);
  const activeScenarioPresence =
    scenarioPresence && scenarioPresence.scenarioName === scenarioName
      ? scenarioPresence
      : null;
  const scenarioCollaborationActive = Boolean(activeScenarioPresence?.collaborationActive);
  const canReleaseScenarioEditing = Boolean(
    backendReachable &&
    activeScenarioPresence?.isEditor &&
    !activeScenarioPresence?.canApproveRequest &&
    currentUser &&
    (currentUser.role === 'SUPERADMIN' || currentUser.role === 'ADMIN'),
  );
  const releaseScenarioEditingLabel =
    canReleaseScenarioEditing && activeScenarioPresence?.returnTarget
      ? t('presence.returnEditing', { name: activeScenarioPresence.returnTarget.displayName })
      : t('presence.releaseEditing');
  const shouldShowScenarioPresenceBanner = shouldShowScenarioPresenceBannerState({
    presence: activeScenarioPresence,
  });
  const canMutateCurrentScenarioOnlineState = canMutateCurrentScenarioOnline(activeScenarioPresence);
  const isScenarioReadOnly = Boolean(
    auth?.authenticated &&
    loadedScenario &&
    scenarioName &&
    !canMutateCurrentScenarioOnlineState,
  );
  const canMutateCurrentScenario = !isScenarioReadOnly;

  // Reference to the new product name input to enable focusing it




  // Populate the product suggestions datalist once on mount. This
  // effect clears any existing options on the datalist with id
  // "product-options" and appends a new option for each entry in
  // menuItems. Each option uses the item's id as its value and
  // displays the human readable name as its text. Running this in
  // useEffect ensures it only executes once after the component
  // mounts and after the DOM is ready.
  useEffect(() => {
    const listElement = document.getElementById('product-options');
    if (listElement) {
      // Remove any existing children
      while (listElement.firstChild) {
        listElement.removeChild(listElement.firstChild);
      }
      // Append options from menuItems. Use the human‑readable name as the value so that
      // the browser datalist displays only the name. We leave the text content empty
      // because Chrome will show the value as the visible suggestion. Mapping back to
      // the corresponding id happens when adding a product.
      const productOptions = effectiveMenuItems;
      productOptions.forEach(item => {
        const option = document.createElement('option');
        option.value = item.name;
        // Some browsers may display the textContent; keep it the same as the value.
        option.textContent = item.name;
        // store the id on a data attribute for possible future use
        option.setAttribute('data-id', String(item.id));
        listElement.appendChild(option);
      });
    }
    // no dependencies: run only once
  }, [effectiveMenuItems]);

  // Removed file/settings menu outside-click detection: handled in SystemBar component

  // Keyboard shortcuts: global handler for scenario/search modals, file/settings menus,
  // and saving scenarios. Table edit modal key handling is delegated to
  // handleTableModalKeyDown.  We also keep the new product input focused when the modal is open.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      // Global Ctrl+S/Cmd+S saves the current scenario
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        if (canMutateCurrentScenario) {
          saveScenario();
        }
        return;
      }
      // Escape closes the scenario modal if open
      if (e.key === 'Escape') {
        if (showScenarioModal) {
          setShowScenarioModal(false);
        }
        return;
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [canMutateCurrentScenario, showScenarioModal, saveScenario]);

  // Removed search handler; search is now handled within the SearchBox component.

  // Event handler when clicking a table in the grid or list.  Prevent
  // click if a grid drag/resize or list drag is in progress.  In
  // delete mode, remove the table and record history.  Otherwise open
  // the edit modal.
  function handleTableClick(table: Table): void {
    // Prevent click if a grid drag/resize is in progress
    if (isDragging.current) return;
    if (isScenarioReadOnly) {
      openTableModal(table);
    } else if (mode === 'delete') {
      // Remove the selected table and record history. Use a non-callback
      // version of setTables so that the deletion logic remains simple.
      const updated = tables.filter(tbl => tbl.id !== table.id);
      const description = t('table.deletedHistory', { name: table.name });
      setTables(updated);
      pushHistory(updated, walls, description);
      // Auto‑save after deletion
      saveScenario(true, { tables: updated }, { historyDescription: description });
    } else {
      openTableModal(table);
    }
  }

  // Layout is computed within the TableGrid component; no need to define it here.

  const handleScenarioSelect = (name: string): void => {
    loadScenarioByName(name);
  };

  const requiresSensitivePassword = Boolean(
    auth?.authenticated &&
    auth.authMethod === 'pin' &&
    !auth.sensitiveAccessGranted,
  );

  function openSensitiveArea(area: 'profile' | 'users' | 'products' | 'productCategories') {
    if (requiresSensitivePassword) {
      setPendingSensitiveModal(area);
      return;
    }
    if (area === 'profile') {
      setShowProfileModal(true);
      return;
    }
    if (area === 'products') {
      setShowManageProductsModal(true);
      return;
    }
    if (area === 'productCategories') {
      setShowManageProductCategoriesModal(true);
      return;
    }
    setShowManageUsersModal(true);
  }

  async function handleAuthorizeSensitiveAccess(password: string) {
    setAuthorizingSensitiveAccess(true);
    try {
      await authorizeSensitivePassword(password);
      if (pendingSensitiveModal === 'profile') {
        setShowProfileModal(true);
      } else if (pendingSensitiveModal === 'products') {
        setShowManageProductsModal(true);
      } else if (pendingSensitiveModal === 'productCategories') {
        setShowManageProductCategoriesModal(true);
      } else if (pendingSensitiveModal === 'users') {
        setShowManageUsersModal(true);
      }
      setPendingSensitiveModal(null);
    } finally {
      setAuthorizingSensitiveAccess(false);
    }
  }

  function openCreateScenarioModal() {
    setScenarioSettingsMode('create');
    setScenarioSettingsInitialName('');
    setScenarioSettingsInitialGridSize({ cols: 16, rows: 17 });
    setScenarioSettingsInitialIsDefault(false);
    setScenarioSettingsInitialShowPrices(false);
    setScenarioSettingsInitialItemizePrintItems(false);
    setShowScenarioSettingsModal(true);
  }

  function openScenarioLibraryModal() {
    setShowScenarioModal(true);
    if (auth?.authenticated && backendReachable) {
      void refreshScenarioNamesFromServer();
    }
  }

  function openArchivedScenariosModal() {
    setShowArchivedScenariosModal(true);
    if (auth?.authenticated && backendReachable) {
      void refreshScenarioNamesFromServer();
    }
  }

  function openEditScenarioModal() {
    setScenarioSettingsMode('edit');
    setScenarioSettingsInitialName(scenarioName);
    setScenarioSettingsInitialGridSize(gridSize);
    setScenarioSettingsInitialIsDefault(authScenario?.isDefault ?? false);
    setScenarioSettingsInitialShowPrices(showPrices);
    setScenarioSettingsInitialItemizePrintItems(itemizePrintItems);
    setShowScenarioSettingsModal(true);
  }

  function openDuplicateScenarioModal() {
    if (!loadedScenario || !scenarioName) {
      return;
    }
    setScenarioSettingsMode('duplicate');
    setScenarioSettingsInitialName(`${scenarioName}_copy`);
    setScenarioSettingsInitialGridSize(gridSize);
    setScenarioSettingsInitialIsDefault(false);
    setScenarioSettingsInitialShowPrices(showPrices);
    setScenarioSettingsInitialItemizePrintItems(itemizePrintItems);
    setShowScenarioSettingsModal(true);
  }

  async function handleSaveScenarioBaseState() {
    if (!scenarioName) {
      return;
    }

    setScenarioPresenceActionLoading(true);
    try {
      await flushScenarioWrites();
      const syncResult = await syncPendingMutations(scenarioName);
      if (syncResult.status !== 'idle') {
        throw new Error(syncResult.lastError ?? t('scenario.baseStateSaveError'));
      }
      const updatedSnapshot = await saveScenarioBaseStateRequest(scenarioName);
      applyScenarioSnapshot(updatedSnapshot);
      showToast(t('scenario.baseStateSaveSuccess'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('scenario.baseStateSaveError'), 'error');
    } finally {
      setScenarioPresenceActionLoading(false);
    }
  }

  async function handleResetScenarioToBaseState() {
    if (!scenarioName) {
      return;
    }

    try {
      const baseState = await loadScenarioBaseState(scenarioName);
      if (!baseState) {
        showToast(t('scenario.baseStateMissingError'), 'warning');
        return;
      }

      const description = t('table.resetHistory');
      setGridSize(baseState.gridSize);
      setWalls(baseState.walls);
      setTables(baseState.tables);
      setShowPrices(baseState.showPrices);
      setItemizePrintItems(baseState.itemizePrintItems);
      pushHistory(baseState.tables, baseState.walls, description);
      saveScenario(
        true,
        {
          gridSize: baseState.gridSize,
          walls: baseState.walls,
          tables: baseState.tables,
          showPrices: baseState.showPrices,
          itemizePrintItems: baseState.itemizePrintItems,
        },
        { historyDescription: description },
      );
      showToast(t('scenario.resetSuccess'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('scenario.resetError'), 'error');
    }
  }

  function applyScenarioSnapshot(snapshot: ScenarioSnapshot): void {
    if (!snapshot) {
      return;
    }
    setScenarioName(snapshot.scenarioName);
    setScenarioVersion(snapshot.currentVersion ?? null);
    setGridSize(snapshot.gridSize);
    setWalls(snapshot.walls);
    setTables(snapshot.tables);
    setShowPrices(snapshot.showPrices);
    setItemizePrintItems(snapshot.itemizePrintItems);
    setLoadedScenario(true);
    setHistorySyncToken((current) => current + 1);
  }

  function applyPresenceUpdate(nextPresence: ScenarioEditPresenceRecord | null) {
    const previousPresence = scenarioPresenceRef.current;
    setScenarioPresence(nextPresence);
    if (
      previousPresence?.scenarioName === nextPresence?.scenarioName &&
      previousPresence?.isEditor &&
      nextPresence &&
      !nextPresence.canEdit
    ) {
      showToast(t('presence.lockLost'), 'warning');
    }
  }

  async function refreshScenarioIfViewerIsStale(
    nextPresence: ScenarioEditPresenceRecord | null,
    targetScenarioName: string,
  ) {
    if (
      !shouldRefreshScenarioForPresence({
        nextPresence,
        currentVersion: scenarioVersionRef.current,
      })
    ) {
      return;
    }

    const currentSyncState = await loadSyncState();
    if (currentSyncState.conflictCount > 0 || currentSyncState.pendingCount > 0) {
      return;
    }

    await bootstrapFromServer(targetScenarioName);
    await refreshFromLocal();
    const latestSnapshot = await loadScenarioSnapshot(targetScenarioName);
    if (latestSnapshot) {
      applyScenarioSnapshot(latestSnapshot);
    }
  }

  useEffect(() => {
    if (!loadedScenario || !scenarioName || !auth?.authenticated) {
      setScenarioPresence(null);
      setScenarioPresenceLoading(false);
      return;
    }

    if (!backendReachable) {
      setScenarioPresenceLoading(false);
      return;
    }

    let cancelled = false;
    const lockedScenarioName = scenarioName;
    let pollTimeoutId: number | undefined;

    function scheduleNextPoll() {
      if (cancelled) {
        return;
      }

      const delayMs = getPresencePollIntervalMs(scenarioPresenceRef.current);
      pollTimeoutId = window.setTimeout(() => {
        void pollPresence();
      }, delayMs);
    }

    async function bootstrapPresence() {
      setScenarioPresenceLoading(true);
      try {
        const presence = await acquireScenarioEditLock(lockedScenarioName);
        if (!cancelled) {
          settleScenarioAutoClaim(presence);
          applyPresenceUpdate(presence);
          await refreshScenarioIfViewerIsStale(presence, lockedScenarioName);
        }
      } catch (error) {
        if (!cancelled) {
          applyPresenceUpdate(null);
        }
      } finally {
        if (!cancelled) {
          setScenarioPresenceLoading(false);
          scheduleNextPoll();
        }
      }
    }

    async function pollPresence() {
      try {
        const latestPresence =
          scenarioPresenceRef.current?.scenarioName === lockedScenarioName && scenarioPresenceRef.current?.isEditor
            ? await renewScenarioEditLock(lockedScenarioName)
            : await loadScenarioEditPresence(lockedScenarioName);
        const resolvedPresence =
          (shouldRecoverEditorLock(latestPresence) || shouldAutoClaimScenario(latestPresence))
            ? await acquireScenarioEditLock(lockedScenarioName)
            : latestPresence;
        if (!cancelled) {
          settleScenarioAutoClaim(resolvedPresence);
          applyPresenceUpdate(resolvedPresence);
          await refreshScenarioIfViewerIsStale(resolvedPresence, lockedScenarioName);
        }
      } catch (error) {
        if (!cancelled) {
          applyPresenceUpdate(null);
        }
      } finally {
        scheduleNextPoll();
      }
    }

    void bootstrapPresence();

    return () => {
      cancelled = true;
      if (pollTimeoutId) {
        window.clearTimeout(pollTimeoutId);
      }
      const currentPresence = scenarioPresenceRef.current;
      if (
        backendReachableRef.current &&
        currentPresence?.scenarioName === lockedScenarioName &&
        currentPresence.isEditor
      ) {
        void releaseScenarioEditLock(lockedScenarioName).catch(() => undefined);
      }
    };
  }, [auth?.authenticated, backendReachable, loadedScenario, scenarioName, t]);

  useEffect(() => {
    if (typeof window === 'undefined' || !auth?.authenticated) {
      return;
    }

    const handlePageHide = () => {
      const currentPresence = scenarioPresenceRef.current;
      if (
        !backendReachableRef.current ||
        !currentPresence?.isEditor ||
        !currentPresence.scenarioName
      ) {
        return;
      }

      void releaseScenarioEditLock(currentPresence.scenarioName, { keepalive: true }).catch(() => undefined);
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [auth?.authenticated]);

  useEffect(() => {
    if (
      activeScenarioPresence?.scenarioName !== scenarioName ||
      activeScenarioPresence?.currentVersion == null
    ) {
      return;
    }

    if (scenarioVersionRef.current == null || activeScenarioPresence.currentVersion > scenarioVersionRef.current) {
      setScenarioVersion(activeScenarioPresence.currentVersion);
    }
  }, [activeScenarioPresence?.currentVersion, activeScenarioPresence?.scenarioName, scenarioName]);

  useEffect(() => {
    if (isScenarioReadOnly && mode !== null) {
      setMode(null);
    }
  }, [isScenarioReadOnly, mode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handlePresenceRealtime = (event: Event) => {
      const detail = (event as CustomEvent<RealtimeEventPayload>).detail;
      if (!detail || detail.type !== 'scenario-presence' || detail.scenarioName !== scenarioName || !backendReachable) {
        return;
      }
      void loadScenarioEditPresence(scenarioName)
        .then(async (presence) => {
          const resolvedPresence =
            (shouldRecoverEditorLock(presence) || shouldAutoClaimScenario(presence))
              ? await acquireScenarioEditLock(scenarioName)
              : presence;
          settleScenarioAutoClaim(resolvedPresence);
          applyPresenceUpdate(resolvedPresence);
          await refreshScenarioIfViewerIsStale(resolvedPresence, scenarioName);
        })
        .catch(() => undefined);
    };

    const handleSnapshotRealtime = (event: Event) => {
      const detail = (event as CustomEvent<RealtimeEventPayload>).detail;
      if (!detail || detail.type !== 'scenario-snapshot' || detail.scenarioName !== scenarioName || !backendReachable) {
        return;
      }
      if (!loadedScenario || canMutateCurrentScenario || !activeScenarioPresence?.collaborationActive) {
        return;
      }

      void (async () => {
        try {
          const syncState = await loadSyncState();
          if (syncState.conflictCount > 0 || syncState.pendingCount > 0) {
            return;
          }
          await bootstrapFromServer(scenarioName);
          await refreshFromLocal();
          const latestSnapshot = await loadScenarioSnapshot(scenarioName);
          if (latestSnapshot) {
            applyScenarioSnapshot(latestSnapshot);
          }
        } catch (_error) {
          // Keep the current view if a realtime refresh fails.
        }
      })();
    };

    window.addEventListener(REALTIME_SCENARIO_PRESENCE_EVENT, handlePresenceRealtime as EventListener);
    window.addEventListener(REALTIME_SCENARIO_SNAPSHOT_EVENT, handleSnapshotRealtime as EventListener);

    return () => {
      window.removeEventListener(REALTIME_SCENARIO_PRESENCE_EVENT, handlePresenceRealtime as EventListener);
      window.removeEventListener(REALTIME_SCENARIO_SNAPSHOT_EVENT, handleSnapshotRealtime as EventListener);
    };
  }, [
    activeScenarioPresence?.collaborationActive,
    backendReachable,
    canMutateCurrentScenario,
    loadedScenario,
    refreshFromLocal,
    scenarioName,
  ]);

  useEffect(() => {
    const shouldRetryLockedSync =
      syncState.status === 'locked' &&
      syncState.pendingCount > 0 &&
      Boolean(scenarioName);

    if (!shouldRetryLockedSync || !scenarioName) {
      syncRetryKeyRef.current = null;
      return;
    }

    if (!backendReachable || !activeScenarioPresence?.isEditor) {
      return;
    }

    const retryKey = [
      scenarioName,
      syncState.pendingCount,
      syncState.lastError ?? '',
      activeScenarioPresence.currentVersion,
    ].join('|');

    if (syncRetryKeyRef.current === retryKey) {
      return;
    }

    syncRetryKeyRef.current = retryKey;

    void (async () => {
      try {
        await syncPendingMutations(scenarioName);
      } finally {
        await refreshFromLocal();
      }
    })();
  }, [
    activeScenarioPresence?.isEditor,
    activeScenarioPresence?.currentVersion,
    backendReachable,
    refreshFromLocal,
    scenarioName,
    syncState.lastError,
    syncState.pendingCount,
    syncState.status,
  ]);

  useEffect(() => {
    const shouldHealConflict = shouldAutoHealSoloEditorConflict({
      presence: activeScenarioPresence,
      backendReachable,
      scenarioName,
      syncState: {
        status: syncState.status,
        conflictCount: syncState.conflictCount,
        conflictScenarioName: syncState.conflictScenarioName,
      },
    });

    if (!shouldHealConflict) {
      soloConflictHealKeyRef.current = null;
      return;
    }

    const healKey = [
      scenarioName,
      syncState.conflictCount,
      syncState.conflictRemoteVersion ?? 'none',
      syncState.lastError ?? '',
      activeScenarioPresence?.currentVersion ?? 'none',
    ].join('|');

    if (soloConflictHealKeyRef.current === healKey) {
      return;
    }

    soloConflictHealKeyRef.current = healKey;

    void (async () => {
      try {
        const overwrittenSnapshot = await overwriteLatestScenarioConflictWithLocalSnapshot();
        await refreshFromLocal();
        if (overwrittenSnapshot) {
          applyScenarioSnapshot(overwrittenSnapshot);
        }
      } catch (_error) {
        // Leave the visible conflict banner in place if the automatic self-heal fails.
      }
    })();
  }, [
    activeScenarioPresence,
    backendReachable,
    refreshFromLocal,
    scenarioName,
    syncState.conflictCount,
    syncState.conflictRemoteVersion,
    syncState.conflictScenarioName,
    syncState.lastError,
    syncState.status,
  ]);

  async function handleLoadLatestConflict() {
    setConflictActionLoading(true);
    try {
      const resolvedSnapshot = await resolveLatestScenarioConflict();
      await refreshFromLocal();
      if (resolvedSnapshot) {
        applyScenarioSnapshot(resolvedSnapshot);
      }
      showToast(t('status.conflictResolvedWithLatest'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('status.conflictResolveError'), 'error');
    } finally {
      setConflictActionLoading(false);
    }
  }

  async function handleKeepConflictAsCopy() {
    setConflictActionLoading(true);
    try {
      const conflictDraft = await getLatestConflictScenarioDraft();
      if (!conflictDraft) {
        showToast(t('status.conflictCopyError'), 'error');
        return;
      }

      setScenarioSettingsMode('conflictCopy');
      setScenarioSettingsInitialName(conflictDraft.suggestedName);
      setScenarioSettingsInitialGridSize(conflictDraft.snapshot.gridSize);
      setScenarioSettingsInitialIsDefault(false);
      setScenarioSettingsInitialShowPrices(conflictDraft.snapshot.showPrices);
      setScenarioSettingsInitialItemizePrintItems(conflictDraft.snapshot.itemizePrintItems);
      setShowScenarioSettingsModal(true);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('status.conflictCopyError'), 'error');
    } finally {
      setConflictActionLoading(false);
    }
  }

  async function handleOverwriteConflictWithMine() {
    setConflictActionLoading(true);
    try {
      const overwrittenSnapshot = await overwriteLatestScenarioConflictWithLocalSnapshot();
      await refreshFromLocal();
      if (overwrittenSnapshot) {
        applyScenarioSnapshot(overwrittenSnapshot);
      }
      showToast(t('status.conflictOverwriteSuccess'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('status.conflictOverwriteError'), 'error');
    } finally {
      setConflictActionLoading(false);
    }
  }

  async function handleScenarioTakeOver() {
    if (!scenarioName) {
      return;
    }
    setScenarioPresenceActionLoading(true);
    try {
      const presence = await acquireScenarioEditLock(scenarioName, { force: true });
      setScenarioPresence(presence);
      showToast(t('presence.takeOverSuccess'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('presence.takeOverError'), 'error');
    } finally {
      setScenarioPresenceActionLoading(false);
    }
  }

  async function handleScenarioClaimEditing() {
    if (!scenarioName) {
      return;
    }
    setScenarioPresenceActionLoading(true);
    try {
      const presence = await acquireScenarioEditLock(scenarioName);
      setScenarioPresence(presence);
      if (presence.isEditor) {
        showToast(t('presence.claimEditingSuccess'), 'success');
      } else {
        showToast(t('presence.claimEditingError'), 'warning');
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('presence.claimEditingError'), 'error');
    } finally {
      setScenarioPresenceActionLoading(false);
    }
  }

  async function handleScenarioReleaseEditing() {
    if (!scenarioName) {
      return;
    }
    setScenarioPresenceActionLoading(true);
    const returnTargetName = activeScenarioPresence?.returnTarget?.displayName ?? null;
    try {
      if (activeScenarioPresence?.returnTarget) {
        const pendingPresence = await setScenarioReleasePending(scenarioName, true);
        setScenarioPresence(pendingPresence);
      }
      await flushScenarioWrites();
      const syncResult = await syncPendingMutations(scenarioName);
      if (syncResult.status !== 'idle' || syncResult.pendingCount > 0 || syncResult.conflictCount > 0) {
        throw new Error(t('presence.releaseEditingSyncError'));
      }
      await refreshFromLocal();
      const presence = await releaseScenarioEditLock(scenarioName);
      setScenarioPresence(presence);
      showToast(
        returnTargetName
          ? t('presence.returnEditingSuccess', { name: returnTargetName })
          : t('presence.releaseEditingSuccess'),
        'success',
      );
    } catch (error) {
      if (activeScenarioPresence?.returnTarget) {
        try {
          const restoredPresence = await setScenarioReleasePending(scenarioName, false);
          setScenarioPresence(restoredPresence);
        } catch {
          // Leave presence refresh to polling/SSE if the temporary release-pending clear fails.
        }
      }
      showToast(error instanceof Error ? error.message : t('presence.releaseEditingError'), 'error');
    } finally {
      setScenarioPresenceActionLoading(false);
    }
  }

  async function handleScenarioRequestAccess() {
    if (!scenarioName) {
      return;
    }
    setScenarioPresenceActionLoading(true);
    try {
      const presence = await requestScenarioEditAccess(scenarioName);
      setScenarioPresence(presence);
      showToast(t('presence.requestSent'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('presence.requestError'), 'error');
    } finally {
      setScenarioPresenceActionLoading(false);
    }
  }

  async function handleApproveScenarioRequest() {
    if (!scenarioName) {
      return;
    }
    setScenarioPresenceActionLoading(true);
    try {
      await flushScenarioWrites();
      const syncResult = await syncPendingMutations(scenarioName);
      if (syncResult.status !== 'idle' || syncResult.pendingCount > 0 || syncResult.conflictCount > 0) {
        throw new Error(t('presence.requestApproveSyncError'));
      }
      await refreshFromLocal();
      const presence = await respondScenarioEditAccessRequest(scenarioName, true);
      setScenarioPresence(presence);
      showToast(t('presence.requestApproved'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('presence.requestApproveError'), 'error');
    } finally {
      setScenarioPresenceActionLoading(false);
    }
  }

  async function handleDenyScenarioRequest(reason: string) {
    if (!scenarioName) {
      return;
    }
    setScenarioPresenceActionLoading(true);
    try {
      const presence = await respondScenarioEditAccessRequest(scenarioName, false, reason);
      setScenarioPresence(presence);
      showToast(t('presence.requestDenied'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('presence.requestDenyError'), 'error');
    } finally {
      setScenarioPresenceActionLoading(false);
      setShowDenyAccessReasonModal(false);
    }
  }

  // Render the component
  return (
    <div id="app" className={darkMode ? 'dark' : ''}>
      <SystemStatusBanner
        onLoadLatestConflict={handleLoadLatestConflict}
        onKeepConflictAsCopy={handleKeepConflictAsCopy}
        onOverwriteConflictWithMine={handleOverwriteConflictWithMine}
        conflictActionLoading={conflictActionLoading}
      />
      {shouldShowScenarioPresenceBanner ? (
        <ScenarioPresenceBanner
          presence={activeScenarioPresence}
          loading={scenarioPresenceActionLoading || scenarioPresenceLoading}
          canReleaseEditing={canReleaseScenarioEditing}
          releaseEditingLabel={releaseScenarioEditingLabel}
          onClaimEditing={handleScenarioClaimEditing}
          onReleaseEditing={handleScenarioReleaseEditing}
          onTakeOver={handleScenarioTakeOver}
          onRequestAccess={handleScenarioRequestAccess}
          onApproveRequest={handleApproveScenarioRequest}
          onDenyRequest={() => setShowDenyAccessReasonModal(true)}
        />
      ) : null}
      {/* System bar replaced by component */}
      <SystemBar
        currentVersion={currentVersion}
        currentUser={currentUser}
        darkMode={darkMode}
        onToggleDarkMode={toggleDarkMode}
        canSeeAppMenu={canSeeAppMenu(currentUser)}
        canDeleteScenario={canDeleteScenario(currentUser)}
        loadedScenario={loadedScenario}
        scenarioReadOnly={isScenarioReadOnly}
        onFileLoad={openScenarioLibraryModal}
        onFileNew={openCreateScenarioModal}
        onDuplicateScenario={openDuplicateScenarioModal}
        onSaveScenarioBaseState={handleSaveScenarioBaseState}
        onFileDelete={() => setShowDeleteScenarioModal(true)}
        onArchiveScenario={archiveCurrentScenario}
        onEditScenario={openEditScenarioModal}
        onShowArchivedScenarios={openArchivedScenariosModal}
        recentScenarioNames={recentScenarioNames}
        onFileLoadRecent={loadRecentScenario}
        onFileReset={handleResetScenarioToBaseState}
        onManageProducts={() => openSensitiveArea('products')}
        onManageProductCategories={() => openSensitiveArea('productCategories')}
        onManageUsers={() => openSensitiveArea('users')}
        onOpenProfile={() => openSensitiveArea('profile')}
        onLogout={async () => {
          if (scenarioPresenceRef.current?.isEditor && scenarioPresenceRef.current?.scenarioName) {
            await releaseScenarioEditLock(scenarioPresenceRef.current.scenarioName).catch(() => undefined);
          }
          await logout();
          showToast(t('app.loggedOut'), 'success');
        }}
      />
      <Header
        scenarioName={scenarioName}
        tables={tables}
        onSelectTable={(table) => {
          if (isScenarioReadOnly) {
            openTableModal(table);
          } else if (mode === 'delete') {
            deleteTable(table);
          } else {
            openTableModal(table);
          }
        }}
        showToast={showToast}
        headerRef={headerRef}
      />
      {/* Main content */}
      <div className="content">
        {/* Table list replaced by component */}
        <TableList
          tables={tables}
          defaultTableColor={currentTableColor}
          readOnly={isScenarioReadOnly}

          onReorder={reorderTables}

          onTableClick={handleTableClick}
        />
        {/* Grid container. We set its height equal to the computed layout height (rows * rowHeight) so that items can be dragged downwards across all rows. */}
        <div className="grid-container" ref={gridRef}>
          {/* Grid background lines */}
          <div
            className="grid-lines"
            style={{
              backgroundImage: `linear-gradient(to right, #eee 1px, transparent 1px), linear-gradient(to bottom, #eee 1px, transparent 1px)`,
              backgroundSize: `${100 / gridSize.cols}% 100%, 100% ${100 / gridSize.rows}%`,
            }}
          ></div>
          {/* Delete mode indicator shows prominently when the delete tool is active */}
          {mode === 'delete' && (
            <ToolModeBanner
              variant="delete"
              message={t('indicators.deleteMode')}
              exitLabel={t('toolbar.stop')}
              onExit={() => setMode(null)}
            />
          )}
          {/* Wall mode indicator shows when the wall tool is active. Provides a stop button to exit drawing mode. */}
          {mode === 'wall' && (
            <ToolModeBanner
              variant="wall"
              message={t('indicators.wallMode')}
              exitLabel={t('toolbar.stop')}
              onExit={() => setMode(null)}
            />
          )}
          {/* Table grid encapsulating ReactGridLayout and table interactions */}
          <TableGrid
            tables={tables}
            gridSize={gridSize}
            mode={mode}
            readOnly={isScenarioReadOnly}
            defaultTableColor={currentTableColor}
            onTablesChange={(updated, description) => {
              setTables(updated);
              if (description) {
                pushHistory(updated, walls, description);
                saveScenario(true, { tables: updated }, { historyDescription: description });
              }
            }}
            onEditTable={(table) => openTableModal(table)}

            onDeleteTable={deleteTable}

            isDraggingRef={isDragging}
            gridRef={gridRef}
            layoutHeight={rglHeightPx}
          />
          {/* Walls layer handles rendering existing walls and capturing pointer events when drawing new walls. Placed after TableGrid so that the overlay sits on top of the grid. */}
          <WallsLayer
            mode={mode}
            readOnly={isScenarioReadOnly}
            walls={walls}
            setWalls={setWalls}
            tables={tables}
            pushHistory={pushHistory}
            saveScenario={saveScenario}
            currentWallColor={currentWallColor}
            gridRef={gridRef}
            uniqueId={uniqueId}
          />
          {/* Last action message shown at bottom left when undo/redo occurs */}
          {lastActionMessage && <div className="last-action">{lastActionMessage}</div>}
        </div>
        {/* Toolbar replaced by component */}
        <Toolbar
          undo={handleUndo}
          redo={handleRedo}
          canUndo={historyIndex > 0}
          canRedo={historyIndex < history.length - 1}
          readOnly={isScenarioReadOnly}
          mode={mode}
          toggleDeleteTool={toggleDeleteTool}
          toggleWallTool={toggleWallTool}
          handleAddTable={addTable}
          tableColor={currentTableColor}
          wallColor={currentWallColor}
          handleChangeTableColor={handleChangeTableColor}
          handleChangeWallColor={handleChangeWallColor}
        />
      </div>
      {/* Table edit modal. Pass the selected table and callbacks for saving and closing. */}
      {showTableModal && selectedTable && (
        <TableModal
          table={selectedTable}
          defaultTableColor={currentTableColor}
          readOnly={isScenarioReadOnly}
          showPrices={showPrices}
          itemizePrintItems={itemizePrintItems}
          onSave={updatedTable => {
            updateTable(updatedTable);
          }}
          onAutoSave={updatedTable => {
            updateTable(updatedTable, { recordHistory: false });
          }}
          onClose={() => {
            closeTableModal();
          }}
          showToast={showToast}
          menuItems={effectiveMenuItems}
        />
      )}
      {/* Scenario load modal */}
      {showScenarioModal && (
        <ScenarioModal
          scenarios={scenarioNames}
          onSelectScenario={handleScenarioSelect}
          onClose={() => setShowScenarioModal(false)}
        />
      )}
      {showArchivedScenariosModal && (
        <ScenarioModal
          title={t('scenario.archivedTitle')}
          description={t('scenario.archivedDescription')}
          emptyMessage={t('scenario.archivedEmpty')}
          scenarios={archivedScenarioNames}
          onSelectScenario={(name) => restoreArchivedScenario(name)}
          onClose={() => setShowArchivedScenariosModal(false)}
        />
      )}
      {showScenarioSettingsModal && (
        <ScenarioSettingsModal
          title={
            scenarioSettingsMode === 'create'
              ? t('scenario.newTitle')
              : scenarioSettingsMode === 'conflictCopy'
                ? t('status.keepAsCopy')
              : scenarioSettingsMode === 'duplicate'
                ? t('scenario.duplicateTitle')
                : t('scenario.editTitle')
          }
          saveLabel={
            scenarioSettingsMode === 'create'
              ? t('scenario.createButton')
              : scenarioSettingsMode === 'conflictCopy'
                ? t('status.keepAsCopy')
              : scenarioSettingsMode === 'duplicate'
                ? t('scenario.duplicateButton')
                : t('scenario.saveButton')
          }
          scenarioName={scenarioSettingsInitialName}
          gridSize={scenarioSettingsInitialGridSize}
          isDefault={scenarioSettingsInitialIsDefault}
          showPrices={scenarioSettingsInitialShowPrices}
          itemizePrintItems={scenarioSettingsInitialItemizePrintItems}
          canSetDefaultScenario={canSetDefaultScenario(currentUser)}
          onClose={() => setShowScenarioSettingsModal(false)}
          onSave={(input) => {
            if (scenarioSettingsMode === 'create') {
              createScenario({
                nextName: input.nextName,
                gridCols: input.gridCols,
                gridRows: input.gridRows,
                isDefault: input.isDefault,
                showPrices: input.showPrices,
                itemizePrintItems: input.itemizePrintItems,
              });
            } else if (scenarioSettingsMode === 'conflictCopy') {
              setConflictActionLoading(true);
              void (async () => {
                try {
                  const copiedSnapshot = await keepLatestConflictAsScenarioCopy(input.nextName, {
                    showPrices: input.showPrices,
                    itemizePrintItems: input.itemizePrintItems,
                  });
                  await refreshFromLocal();
                  if (copiedSnapshot) {
                    applyScenarioSnapshot(copiedSnapshot);
                    showToast(t('status.conflictSavedAsCopy', { name: copiedSnapshot.scenarioName }), 'success');
                  }
                } catch (error) {
                  showToast(error instanceof Error ? error.message : t('status.conflictCopyError'), 'error');
                } finally {
                  setConflictActionLoading(false);
                }
              })();
            } else if (scenarioSettingsMode === 'duplicate') {
              duplicateScenario({
                nextName: input.nextName,
                gridCols: input.gridCols,
                gridRows: input.gridRows,
                isDefault: input.isDefault,
                showPrices: input.showPrices,
                itemizePrintItems: input.itemizePrintItems,
              });
            } else {
              editScenario(input);
            }
            setShowScenarioSettingsModal(false);
          }}
        />
      )}
      {showManageUsersModal && currentUser && (
        <ManageUsersModal
          currentUser={currentUser}
          onClose={() => setShowManageUsersModal(false)}
          showToast={showToast}
        />
      )}
      {showDeleteScenarioModal ? (
        <ConfirmActionModal
          title={t('confirm.deleteTitle')}
          message={t('confirm.deleteScenarioBody', { name: scenarioName })}
          confirmLabel={t('confirm.delete')}
          cancelLabel={t('confirm.cancel')}
          loading={deleteScenarioLoading}
          onCancel={() => {
            if (!deleteScenarioLoading) {
              setShowDeleteScenarioModal(false);
            }
          }}
          onConfirm={() => {
            setDeleteScenarioLoading(true);
            void (async () => {
              try {
                await deleteCurrentScenario();
                setShowDeleteScenarioModal(false);
              } finally {
                setDeleteScenarioLoading(false);
              }
            })();
          }}
        />
      ) : null}
      {showManageProductsModal && (
        <ManageProductsModal
          onClose={() => setShowManageProductsModal(false)}
          onCatalogUpdated={refreshFromLocal}
          showToast={showToast}
        />
      )}
      {showManageProductCategoriesModal && (
        <ManageProductCategoriesModal
          onClose={() => setShowManageProductCategoriesModal(false)}
          onCatalogUpdated={refreshFromLocal}
          showToast={showToast}
        />
      )}
      {showProfileModal && currentUser && (
        <ProfileModal
          currentUser={currentUser}
          onClose={() => setShowProfileModal(false)}
          showToast={showToast}
        />
      )}
      {showDenyAccessReasonModal && (
        <DenyAccessReasonModal
          loading={scenarioPresenceActionLoading}
          onCancel={() => setShowDenyAccessReasonModal(false)}
          onSubmit={handleDenyScenarioRequest}
        />
      )}
      {pendingSensitiveModal && (
        <SensitiveAccessModal
          loading={authorizingSensitiveAccess}
          usernameValue={currentUser?.email}
          onClose={() => setPendingSensitiveModal(null)}
          onSubmit={handleAuthorizeSensitiveAccess}
        />
      )}
      {/* Toast notifications replaced by component */}
      <ToastNotification
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastOpen(false)}
      />
    </div>
  );
}

// Render the application

export default TableArrangementApp;


